import { useState, useCallback, useMemo } from 'react';
import Storage from '../Util/Storage';
import FeatureConfig from '../config/FeaturesConfig';
import useInspireOptions from './useInspireOptions';
import { urls } from '../config/Url';

export enum ProxyStorageKeys {
  ORIGINAL_USER_ID = 'originalUserId',
  PROXY_USER_ID = 'proxyUserId',
  PROXY_USER_NAME = 'proxyUserName',
}

interface ProxyState {
  isProxying: boolean;
  proxyUserName: string;
  proxyUserId: string;
  originalUserId: string;
}
const getInitialProxyState = (): ProxyState => {
  const proxyUserId = Storage.getItem(ProxyStorageKeys.PROXY_USER_ID) || '';
  const originalUserId =
    Storage.getItem(ProxyStorageKeys.ORIGINAL_USER_ID) || '';
  const proxyUserName = Storage.getItem(ProxyStorageKeys.PROXY_USER_NAME) || '';

  const isProxying = !!(
    proxyUserId &&
    proxyUserId !== '' &&
    originalUserId &&
    originalUserId !== ''
  );

  return {
    isProxying,
    proxyUserName,
    proxyUserId,
    originalUserId,
  };
};

const useProxy = () => {
  const [proxyState, setProxyState] =
    useState<ProxyState>(getInitialProxyState);
  const { userID } = useInspireOptions();

  const startProxy = useCallback(
    ({
      proxyUserId,
      proxyUserName,
    }: {
      proxyUserId: string;
      proxyUserName: string;
    }) => {
      const originalUserId = userID;

      // Update localStorage
      Storage.setItem(ProxyStorageKeys.ORIGINAL_USER_ID, originalUserId);
      Storage.setItem(ProxyStorageKeys.PROXY_USER_ID, proxyUserId);
      Storage.setItem(ProxyStorageKeys.PROXY_USER_NAME, proxyUserName);

      // Update state
      setProxyState({
        isProxying: true,
        proxyUserName,
        proxyUserId,
        originalUserId,
      });

      // Reset config and redirect - following existing pattern
      FeatureConfig.resetConfig();
      window.location.href = urls.dashboard;
    },
    [userID]
  );

  const endProxy = useCallback(() => {
    // Clear localStorage
    Storage.setItem(ProxyStorageKeys.ORIGINAL_USER_ID, '');
    Storage.setItem(ProxyStorageKeys.PROXY_USER_ID, '');
    Storage.setItem(ProxyStorageKeys.PROXY_USER_NAME, '');

    // Update state
    setProxyState({
      isProxying: false,
      proxyUserName: '',
      proxyUserId: '',
      originalUserId: '',
    });

    // Reset config and redirect - following existing pattern
    FeatureConfig.resetConfig();
    window.location.href = urls.dashboard;
  }, []);

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      isProxying: proxyState.isProxying,
      proxyUserName: proxyState.proxyUserName,
      proxyUserId: proxyState.proxyUserId,
      originalUserId: proxyState.originalUserId,
      startProxy,
      endProxy,
    }),
    [proxyState, startProxy, endProxy]
  );
};

export default useProxy;
